import { format, parseISO } from "date-fns"
import React from "react"
import { useMarina } from "src/contexts/MarinaContext"

import Badge from "src/components/Badge"
import DataTable from "src/components/DataTable"

import { getDromoImports } from "src/api/DromoImports"

import usePollingInfiniteQuery from "src/hooks/use_polling_infinite_query"

import { capitalize, humanize } from "src/utils/string_helpers"

export const IMPORT_STATUS_COLORS = {
  pending: "gray",
  processing: "yellow",
  completed: "teal",
  failed: "red",
}

const REFETCHABLE_STATUSES = ["pending", "processing"]

const hasRefetchableImports = (imports) =>
  imports?.some((imp) => REFETCHABLE_STATUSES.includes(imp.status))

export default function ImportHistoryTable() {
  const { marinaSlug } = useMarina()

  const { flattenedData: dromoImportsData, isLoading } =
    usePollingInfiniteQuery({
      queryKey: ["marina", marinaSlug, "dromoImports"],
      queryFn: ({ pageParam = 1 }) => getDromoImports({ page: pageParam }),
      getNextPageParam: (lastPage, pages) => {
        if (lastPage?.length) {
          return pages.length + 1
        }
      },
      shouldPoll: hasRefetchableImports,
    })

  const renderFormattedTimestamp = (isoString) => {
    const timestamp = parseISO(isoString)
    const date = format(timestamp, "MM/dd/yyyy")
    const time = format(timestamp, "p")
    return (
      <div>
        <div>{date}</div>
        <div className="text-sm text-gray-600">{time}</div>
      </div>
    )
  }

  const isEmpty = !isLoading && !dromoImportsData?.length

  const colDefs = [
    {
      key: "importType",
      header: "Type",
      render: (row) => capitalize(humanize(row.importType)),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <Badge
          text={capitalize(row.status)}
          color={IMPORT_STATUS_COLORS[row.status] || "gray"}
        />
      ),
    },
    {
      key: "numRows",
      header: "Rows",
      render: (row) => `${row.completedRowsCount} / ${row.numRows}`,
    },
    {
      key: "createdAt",
      header: "Started",
      render: (row) => renderFormattedTimestamp(row.createdAt),
    },
    {
      key: "user",
      header: "User",
      render: (row) => row.user?.name || "-",
    },
  ]

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Recent Imports</h2>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white">
        {isEmpty ? (
          <div className="flex flex-col items-center px-6 py-12 text-center">
            <i className="icon icon-upload text-5xl leading-none text-blue-700" />
            <p className="mb-0 mt-3 text-lg font-semibold text-gray-900">
              No imports yet
            </p>
            <p className="mb-0 mt-1 text-gray-600">
              Choose an import type above to get started.
            </p>
          </div>
        ) : (
          <DataTable
            name="dromoImports"
            rowData={dromoImportsData}
            colDefs={colDefs}
            isLoading={isLoading}
            fullHeight
            autoColumnWidth
            hideSettings
            hideActions
            rowConfig={{
              onClick: (row) => {
                window.location.href = `/manage/${marinaSlug}/dromo_imports/${row.id}`
              },
            }}
          />
        )}
      </div>
    </div>
  )
}
